import { useState, useContext, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { UserContext } from 'contexts/user';

import Checkbox from 'components/checkbox/checkbox';
import Input from 'components/input/input';
import Select from 'components/select/select';
import Deleted from 'components/deleted/deleted';
import NoAccess from 'pages/no_access';

import Loading from 'components/loading/loading';

import { getDocumentById, updateDocument } from 'api/documents';

import { ReactComponent as ArrowRightSVG } from 'icons/arrow-right.svg';
import { TYPES, DOCTOR_TYPES, TECHNICIAN_TYPES } from 'config/document';

const DocumentEdit = ({ match, onMessages }) => {
  const [loading, setLoading] = useState(true);
  const [document, setDocument] = useState({});
  const [deletedByData, setDeletedByData] = useState(null);
  const { user } = useContext(UserContext);

  const history = useHistory();

  useEffect(() => {
    async function fetchData() {
      setLoading(true);

      const doc = await getDocumentById(match.params.id);

      if (doc.deletedAt && doc.deletedBy) {
        setDeletedByData({
          email: doc.deletedBy.email,
          deletedAt: doc.deletedAt,
        });
      }

      setDocument(doc);
      setLoading(false);
    }

    fetchData();
  }, [match.params.id]);

  const [validation, setValidation] = useState({
    name: true,
    type: true,
  });

  const onChange = (e) => {
    const { name, value } = e.target;
    setDocument({
      ...document,
      [name]: value,
    });
  };

  const onSelect = (selectedOption) => {
    setDocument({
      ...document,
      type: selectedOption.id,
    });
  };

  const onCheckboxChange = () => {
    setDocument({
      ...document,
      isShared: !document.isShared,
    });
  };

  const checkValidity = useCallback(() => {
    const validation = {
      name: !!document.name.trim(),
      type: !!document.type.trim(),
    };

    setValidation(validation);

    return Object.values(validation).every(Boolean);
  }, [document]);

  const onSubmit = useCallback(async () => {
    if (checkValidity()) {
      const {
        meta: { messages },
      } = await updateDocument(match.params.id, document);

      onMessages(messages);

      history.push(`/documents/${match.params.id}`);
    }
  }, [checkValidity, document]);

  if (loading) return <Loading />;
  if (document.statusCode === 403) return <NoAccess />;
  if (user.role !== 'admin' && document.user?.email !== user.email)
    return <NoAccess />;
  if (deletedByData) return <Deleted deletedBy={deletedByData} />;

  return (
    <main className="th-main">
      <section className="th-canvas">
        <header className="th-canvas-header">
          <h2 className="th-canvas-title">
            <span
              className="pointer"
              onClick={() => history.push(`/documents/${match.params.id}`)}
            >
              {document.name}
            </span>
            <span>
              <ArrowRightSVG />
            </span>
            Edit
          </h2>
          <section className="view-actions">
            <button className="btn-green" data-cy="submit" onClick={onSubmit}>
              Submit
            </button>
          </section>
        </header>
        <div>
          <h4 className="midlightgrey f-small fw5 ttu mt15">
            Document details
          </h4>
          <div className="pa5 pt4 br4 shadow-1 bg-grouped-table mt2 flex flex-column justify-between">
            <div className="w-100 flex flex-column items-start justify-between margin-between">
              <div className="w-100">
                <Input
                  name="name"
                  label="Name*"
                  value={document.name}
                  valid={validation.name}
                  validationText="This question is required!"
                  onChange={(event) => onChange(event)}
                />
              </div>
              <div className="w-100">
                <Select
                  isMulti={false}
                  valid={true}
                  label="Type*"
                  value={TYPES.find((t) => document.type === t.id) || TYPES[0]}
                  options={
                    user.role === 'doctor'
                      ? DOCTOR_TYPES
                      : user.role === 'technician'
                      ? TECHNICIAN_TYPES
                      : TYPES
                  }
                  className="single-select"
                  onChange={onSelect}
                />
              </div>
              <div className="input-group w-100">
                <label>Patient&apos;s access</label>
                <Checkbox
                  name="shared"
                  label="Shared with patient"
                  checked={document.isShared}
                  valid={true}
                  onChange={onCheckboxChange}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default DocumentEdit;
